import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

export const REMARKS_REJECT_MIN_ERROR = 'Enter at least 2 characters explaining the rejection.';
export const REMARKS_MAX_LENGTH = 500;

export type DecisionAction = 'APPROVE' | 'REJECT';

interface DecisionModalProps {
  open: boolean;
  title: string;
  recordLabel: string;
  recordNumber: string;
  engineerName: string;
  projectName: string;
  remarks: string;
  onRemarksChange: (value: string) => void;
  serverError: string;
  loadingAction: DecisionAction | null;
  onApprove: (remarks: string) => void;
  onReject: (remarks: string) => void;
  onCancel: () => void;
}

function validateRemarks(value: string, decision: DecisionAction): string {
  const trimmed = value.trim();
  if (decision === 'REJECT') {
    if (trimmed.length < 2) return REMARKS_REJECT_MIN_ERROR;
  }
  if (trimmed.length > REMARKS_MAX_LENGTH) return `Remarks cannot exceed ${REMARKS_MAX_LENGTH} characters.`;
  return '';
}

export default function DecisionModal({
  open,
  title,
  recordLabel,
  recordNumber,
  engineerName,
  projectName,
  remarks,
  onRemarksChange,
  serverError,
  loadingAction,
  onApprove,
  onReject,
  onCancel,
}: DecisionModalProps) {
  const [validationError, setValidationError] = useState('');
  const [attemptedAction, setAttemptedAction] = useState<DecisionAction | null>(null);
  const remarksRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) {
      setValidationError('');
      setAttemptedAction(null);
    }
  }, [open]);

  const isSubmitting = loadingAction !== null;

  const handleDecision = (decision: DecisionAction) => {
    const error = validateRemarks(remarks, decision);
    if (error) {
      setValidationError(error);
      setAttemptedAction(decision);
      remarksRef.current?.focus();
      return;
    }
    setValidationError('');
    setAttemptedAction(null);
    const trimmed = remarks.trim();
    if (decision === 'APPROVE') onApprove(trimmed);
    else onReject(trimmed);
  };

  const showRejectHint = !validationError && attemptedAction === null && remarks.trim().length === 0;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-surface-secondary border border-subtle rounded-lg p-3 text-sm space-y-1">
            <p><span className="text-secondary">{recordLabel}</span> <span className="font-medium text-primary">{recordNumber}</span></p>
            <p><span className="text-secondary">Engineer:</span> <span className="font-medium text-primary">{engineerName}</span></p>
            <p><span className="text-secondary">Project:</span> <span className="font-medium text-primary">{projectName}</span></p>
          </div>
          <div>
            <Label htmlFor="decision-remarks" className="block text-sm font-medium text-secondary mb-1">Remarks</Label>
            <Textarea
              id="decision-remarks"
              ref={remarksRef}
              value={remarks}
              onChange={e => { setValidationError(''); setAttemptedAction(null); onRemarksChange(e.target.value); }}
              placeholder="Enter remarks for your decision..."
              rows={3}
              maxLength={REMARKS_MAX_LENGTH}
            />
            {showRejectHint && <p className="text-sm text-secondary mt-1">Required when rejecting.</p>}
            {validationError && <p className="text-sm text-red-600 flex items-center gap-1 mt-1" role="alert"><AlertCircle size={14} />{validationError}</p>}
            {serverError && <p className="text-sm text-red-600 flex items-center gap-1 mt-1" role="alert"><AlertCircle size={14} />{serverError}</p>}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="success" className="flex-1" onClick={() => handleDecision('APPROVE')} disabled={isSubmitting} loading={loadingAction === 'APPROVE'}>
              <CheckCircle2 size={16} className="mr-1" /> Approve
            </Button>
            <Button type="button" variant="destructive" className="flex-1" onClick={() => handleDecision('REJECT')} disabled={isSubmitting} loading={loadingAction === 'REJECT'}>
              <XCircle size={16} className="mr-1" /> Reject
            </Button>
          </div>
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={() => onCancel()} disabled={isSubmitting}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
